import { useSetAtom } from 'jotai'
import { useCallback } from 'react'
import {
  notificationAtom,
  generateNotificationId,
  NotificationSeverity,
} from '../state/notifications'

export function useNotification() {
  const setNotification = useSetAtom(notificationAtom)

  const showNotification = useCallback(
    (message: string, severity: NotificationSeverity = 'info', autoHideDuration = 4000) => {
      setNotification({
        id: generateNotificationId(),
        message,
        severity,
        autoHideDuration,
      })
    },
    [setNotification]
  )

  const showSuccess = useCallback(
    (message: string) => {
      showNotification(message, 'success')
    },
    [showNotification]
  )

  const showError = useCallback(
    (message: string) => {
      showNotification(message, 'error', 6000)
    },
    [showNotification]
  )

  const showWarning = useCallback(
    (message: string) => {
      showNotification(message, 'warning', 5000)
    },
    [showNotification]
  )

  // An info toast may carry one action button. It stays up longer than a plain
  // toast, because the reader has to notice it and decide to click.
  const showInfo = useCallback(
    (message: string, action?: { label: string; onClick: () => void }) => {
      if (!action) {
        showNotification(message, 'info')
        return
      }
      setNotification({
        id: generateNotificationId(),
        message,
        severity: 'info',
        autoHideDuration: 10000,
        action,
      })
    },
    [showNotification, setNotification]
  )

  const showUndoNotification = useCallback(
    (message: string, onUndo: () => void) => {
      setNotification({
        id: generateNotificationId(),
        message,
        severity: 'info',
        autoHideDuration: 5000,
        action: {
          label: 'Undo',
          onClick: onUndo,
        },
      })
    },
    [setNotification]
  )

  const hideNotification = useCallback(() => {
    setNotification(null)
  }, [setNotification])

  return {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showUndoNotification,
    hideNotification,
  }
}
